import React, { useState } from 'react';
import { ArrowLeft, Send, Loader2 } from 'lucide-react';
import { submitFeedback } from '../../services/api';
import { useGenContext } from '../../contexts/GenContext';
import { BTN } from '../../styles/tokens';

interface FeedbackFormProps {
  onBack: () => void;
  currentPageLabel: string;
}

type Rating = 1 | 2 | 3;

const RATING_OPTIONS: { value: Rating; emoji: string; label: string }[] = [
  { value: 1, emoji: '👍', label: '好用' },
  { value: 2, emoji: '😐', label: '普通' },
  { value: 3, emoji: '👎', label: '難用' },
];

const FeedbackForm: React.FC<FeedbackFormProps> = ({ onBack, currentPageLabel }) => {
  const { activeTab, genConfig } = useGenContext();

  const [message, setMessage] = useState('');
  const [rating, setRating] = useState<Rating | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isDone, setIsDone] = useState(false);

  const handleSubmit = async () => {
    if (!message.trim() || isSubmitting) return;

    setIsSubmitting(true);
    try {
      // 組裝 appSettings 快照
      const appSettings = {
        activeTab,
        flowModelId: genConfig.flowModelId,
        answers: genConfig.answers,
      };

      await submitFeedback({
        message: message.trim(),
        rating: rating ?? undefined,
        page: activeTab,
        activityLog: [],
        appSettings,
        userAgent: navigator.userAgent,
      });

      setIsDone(true);
    } catch (err) {
      console.error('[FeedbackForm] 送出失敗:', err);
      alert('送出失敗，請稍後再試。');
    } finally {
      setIsSubmitting(false);
    }
  };

  // 送出成功畫面
  if (isDone) {
    return (
      <div className="flex flex-col items-center justify-center flex-1 gap-4 p-6 text-center">
        <div className="text-4xl">🎉</div>
        <div>
          <p className="text-sm font-semibold text-slate-800 dark:text-slate-100 mb-1">謝謝你的回饋！</p>
          <p className="text-xs text-slate-400 dark:text-slate-500 leading-relaxed">
            你的意見對我們很重要，我們會持續改善。
          </p>
        </div>
        <button
          onClick={onBack}
          className="mt-2 px-4 py-2 rounded-xl text-xs font-medium bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-300 hover:bg-slate-200 dark:hover:bg-slate-700 transition-colors"
        >
          返回對話
        </button>
      </div>
    );
  }

  return (
    <div className="flex flex-col flex-1 overflow-hidden">
      {/* 表單標題列 */}
      <div className="flex items-center gap-2 px-4 py-3 border-b border-slate-200 dark:border-slate-700/60 flex-shrink-0">
        <button
          onClick={onBack}
          className="flex items-center justify-center w-7 h-7 rounded-lg text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-200 hover:bg-slate-200 dark:hover:bg-slate-700 transition-colors"
        >
          <ArrowLeft size={15} />
        </button>
        <p className="text-sm font-medium text-slate-700 dark:text-slate-300">提供回饋</p>
      </div>

      {/* 表單內容 */}
      <div className="flex-1 overflow-y-auto px-4 py-4 space-y-4">
        {/* 情緒快選 */}
        <div>
          <p className="text-xs font-medium text-slate-500 dark:text-slate-400 mb-2">整體感受（選填）</p>
          <div className="flex gap-2">
            {RATING_OPTIONS.map(opt => (
              <button
                key={opt.value}
                onClick={() => setRating(rating === opt.value ? null : opt.value)}
                className={`flex-1 flex flex-col items-center gap-1 py-2.5 rounded-xl border text-xs font-medium transition-all ${
                  rating === opt.value
                    ? 'bg-blue-50 dark:bg-blue-900/40 border-blue-400 dark:border-blue-500/60 text-blue-600 dark:text-blue-300'
                    : 'bg-slate-50 dark:bg-slate-800/60 border-slate-200 dark:border-slate-700/50 text-slate-600 dark:text-slate-400 hover:border-slate-300 dark:hover:border-slate-600'
                }`}
              >
                <span className="text-lg leading-none">{opt.emoji}</span>
                <span>{opt.label}</span>
              </button>
            ))}
          </div>
        </div>

        {/* 意見文字 */}
        <div>
          <p className="text-xs font-medium text-slate-500 dark:text-slate-400 mb-2">你的意見</p>
          <textarea
            value={message}
            onChange={e => setMessage(e.target.value)}
            placeholder="有什麼想告訴我們的嗎？功能建議、問題回報都歡迎…"
            rows={5}
            maxLength={2000}
            className="w-full resize-none bg-white dark:bg-slate-900/80 border border-slate-300 dark:border-slate-700/60 rounded-xl px-3 py-2.5 text-sm text-slate-800 dark:text-slate-200 placeholder-slate-400 dark:placeholder-slate-500 focus:outline-none focus:border-blue-500/60 focus:ring-1 focus:ring-blue-500/20 transition-colors"
          />
          <p className="text-[10px] text-slate-400 dark:text-slate-600 mt-1 text-right">
            {message.length}/2000
          </p>
        </div>

        {/* 自動附帶資訊說明 */}
        <div className="rounded-xl bg-slate-50 dark:bg-slate-800/60 border border-slate-200 dark:border-slate-700/40 px-3 py-2.5">
          <p className="text-[11px] text-slate-500 dark:text-slate-400 leading-relaxed">
            回饋將自動附帶：
            <span className="font-medium text-slate-600 dark:text-slate-300">目前頁面（{currentPageLabel}）</span>
            、操作紀錄，幫助我們快速定位問題。
          </p>
        </div>
      </div>

      {/* 底部送出 */}
      <div className="flex-shrink-0 border-t border-slate-200 dark:border-slate-700/60 bg-slate-50/80 dark:bg-slate-800/50 px-3 py-3">
        <button
          onClick={handleSubmit}
          disabled={!message.trim() || isSubmitting}
          className={`w-full flex items-center justify-center gap-2 py-2.5 rounded-xl ${BTN.primary} text-sm font-medium`}
        >
          {isSubmitting ? (
            <>
              <Loader2 size={14} className="animate-spin" />
              送出中…
            </>
          ) : (
            <>
              <Send size={14} />
              送出回饋
            </>
          )}
        </button>
      </div>
    </div>
  );
};

export default FeedbackForm;
